import React from 'react';
import { useRecommendationTracking } from './useRecommendationTracking';
import { RecommendationType } from '@/types/recommendation-tracking';
import { MessageDisplay } from '../../types/chat-hook-types';

/**
 * Factory function to create recommendation tracking wrappers
 * This standardizes the pattern and eliminates manual wrapper creation
 * Now supports both temporary and database IDs for reliability
 */
export function createRecommendationTrackingWrapper<
  TMutation extends (...args: any[]) => any,
  TParams extends Record<string, any>
>(
  useMutation: TMutation,
  recommendationType: RecommendationType,
  options?: {
    getMetadata?: (params: TParams, result: any) => Record<string, any> | Promise<Record<string, any>>;
    getSavedItemId?: (result: any) => string;
  }
) {
  return (message: MessageDisplay | string) => {
    // Support both new (MessageDisplay) and legacy (string) patterns
    const messageId = typeof message === 'string' ? message : message.id;
    const databaseId = typeof message === 'string' ? undefined : message.databaseId;
    
    const mutation = useMutation() as any; // Type assertion for flexibility
    const { markRecommendationSaved } = useRecommendationTracking(messageId, databaseId);
    
    const mutateWithTracking = async (params: TParams) => {
      try {
        console.log(`[${recommendationType}Tracking] 🚀 STARTING mutateWithTracking with params:`, params);
        console.log(`[${recommendationType}Tracking] 🔍 Message info:`, { messageId, databaseId, messageType: typeof message });
        
        // CRITICAL: Validate database ID before proceeding
        if (!databaseId) {
          console.error(`[${recommendationType}Tracking] BLOCKING: No database ID attached to message when Save ${recommendationType} pressed`, {
            messageId,
            databaseId,
            messageType: typeof message,
            hasMessage: !!message
          });
          throw new Error(`Cannot save ${recommendationType}: Message not yet saved to database. Please wait and try again.`);
        }

        console.log(`[${recommendationType}Tracking] ✅ Database ID validation passed:`, {
          messageId,
          databaseId,
          recommendationType
        });

        // Execute the base mutation
        console.log(`[${recommendationType}Tracking] 🔄 Executing base mutation...`);
        const result = await mutation.mutateAsync(params);
        console.log(`[${recommendationType}Tracking] ✅ Base mutation completed:`, result);
        
        // Extract saved item ID (default to result.id)
        const savedItemId = options?.getSavedItemId?.(result) || result.id;
        console.log(`[${recommendationType}Tracking] 📝 Extracted savedItemId:`, savedItemId);
        
        // Generate metadata (default to params) - now supports async
        console.log(`[${recommendationType}Tracking] 🔍 About to generate metadata...`);
        console.log(`[${recommendationType}Tracking] 🔍 Has getMetadata function:`, !!options?.getMetadata);
        console.log(`[${recommendationType}Tracking] 🔍 getMetadata function type:`, typeof options?.getMetadata);
        
        let metadata;
        if (options?.getMetadata) {
          console.log(`[${recommendationType}Tracking] 🔄 Calling getMetadata function...`);
          try {
            const metadataResult = options.getMetadata(params, result);
            console.log(`[${recommendationType}Tracking] 🔍 getMetadata returned:`, metadataResult);
            console.log(`[${recommendationType}Tracking] 🔍 Is Promise?`, metadataResult instanceof Promise);
            
            metadata = await Promise.resolve(metadataResult);
            console.log(`[${recommendationType}Tracking] ✅ Metadata resolved:`, metadata);
          } catch (metadataError) {
            console.error(`[${recommendationType}Tracking] ❌ Error in getMetadata:`, metadataError);
            throw metadataError;
          }
        } else {
          console.log(`[${recommendationType}Tracking] 📝 Using default metadata (params):`, params);
          metadata = { ...params };
        }
        
        console.log(`[${recommendationType}Tracking] 🔍 Final metadata before tracking:`, metadata);
        
        // Track the recommendation (non-blocking)
        try {
          console.log(`[${recommendationType}Tracking] 🔄 Calling markRecommendationSaved...`);
          await markRecommendationSaved({
            recommendationType,
            savedItemId,
            metadata
          });
          
          console.log(`[${recommendationType}Tracking] ✅ Successfully tracked recommendation:`, {
            messageId,
            databaseId,
            savedItemId,
            recommendationType,
            metadata
          });
        } catch (trackingError) {
          console.warn(`[${recommendationType}Tracking] ⚠️ Failed to track recommendation:`, trackingError);
          // Don't fail the operation - tracking is supplementary
        }
        
        console.log(`[${recommendationType}Tracking] 🎉 mutateWithTracking completed successfully`);
        return result;
      } catch (error) {
        console.error(`[${recommendationType}Tracking] ❌ Mutation failed:`, error);
        throw error;
      }
    };
    
    return {
      mutateWithTracking,
      isGenerating: mutation.isPending || mutation.isLoading || false,
      error: mutation.error || null
    };
  };
}

/**
 * Standardized hook for recommendation card state management
 * Handles saved status checking and state persistence
 * Now supports both temporary and database IDs for reliability
 */
export function useRecommendationCardState(
  message: MessageDisplay | string, 
  recommendationType: RecommendationType
) {
  const [localState, setLocalState] = React.useState<'idle' | 'saving' | 'generating' | 'success' | 'saved' | 'error'>('idle');
  
  // Support both new (MessageDisplay) and legacy (string) patterns
  const messageId = typeof message === 'string' ? message : message.id;
  const databaseId = typeof message === 'string' ? undefined : message.databaseId;
  
  const { isRecommendationSaved } = useRecommendationTracking(messageId, databaseId);
  
  // Check saved status on mount
  React.useEffect(() => {
    const checkSavedStatus = async () => {
      try {
        // CRITICAL: Only check saved status if we have a database ID
        if (!databaseId) {
          console.log(`[${recommendationType}CardState] Skipping saved status check - no database ID:`, {
            messageId,
            databaseId
          });
          return;
        }

        const status = await isRecommendationSaved(recommendationType);
        if (status.isSaved) {
          setLocalState('saved');
        }
      } catch (error) {
        console.warn(`[${recommendationType}CardState] Failed to check saved status:`, error);
        // Silently fail - don't disrupt the UI
      }
    };
    
    checkSavedStatus();
  }, [isRecommendationSaved, recommendationType, databaseId]);
  
  return { 
    localState, 
    setLocalState,
    isRecommendationSaved 
  };
}

/**
 * Configuration for different recommendation types
 * Makes adding new types trivial - just add to this config
 */
export const RECOMMENDATION_CONFIGS = {
  task: { 
    saveText: 'Generate Task',
    savingText: 'Generating...',
    successText: 'Generated!',
    savedText: 'View Task',
    errorText: 'Error',
    theme: 'amber'
  },
  research: { 
    saveText: 'Generate Research',
    savingText: 'Generating...',
    successText: 'Generated!',
    savedText: 'View Research',
    errorText: 'Error',
    theme: 'emerald'
  },
  page: { 
    saveText: 'Generate Document',
    savingText: 'Generating...',
    successText: 'Generated!',
    savedText: 'View Document',
    errorText: 'Error',
    theme: 'purple'
  },
  image: { 
    saveText: 'Generate Image',
    savingText: 'Generating...',
    successText: 'Generated!',
    savedText: 'View Image',
    errorText: 'Error',
    theme: 'purple'
  },
  project: { 
    saveText: 'Create Project',
    savingText: 'Creating...',
    successText: 'Created!',
    savedText: 'View Project',
    errorText: 'Error',
    theme: 'rose'
  }
} as const satisfies Record<RecommendationType, {
  saveText: string;
  savingText: string;
  successText: string;
  savedText: string;
  errorText: string;
  theme: string;
}>; 